// src/components/ui/ActivityDialog.tsx
import React, { useEffect, useState } from 'react';
import { X, MessageCircle, Calendar, Clock, Users, Check, X as XIcon } from 'lucide-react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Button } from './Button';
import { Comment, CommentForm, LikeButton } from './Comments';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
 
import { supabase } from '../../lib/supabase';
import { cn } from '../../utils/cn';
import { useEventStore } from '../../stores/useEventStore';
import { useForumStore } from '../../stores/useForumStore';
import { Dialog } from './Dialog';
import { Textarea } from './textarea';
import { useQueryClient } from '@tanstack/react-query';

export type ActivityType = 'post' | 'news' | 'event';

/* eslint-disable no-unused-vars */
interface ActivityDialogProps {
  activityId: number | null;
  activityType: ActivityType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  showComments?: boolean;
  showLikes?: boolean;
  showAttendance?: boolean;
  showEditButton?: boolean;
}
/* eslint-enable no-unused-vars */

interface ActivityData {
  id: number;
  title: string;
  content: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  author_name: string;
  // Event-specific fields
  event_start?: string;
  event_end?: string;
  explanation?: boolean;
  attendees?: number;
  board_initiated?: boolean;
}

export function ActivityDialog({ 
  activityId, 
  activityType, 
  open, 
  onOpenChange, 
  showComments = true, 
  showLikes = true, 
  showAttendance = false,
  showEditButton = true 
}: ActivityDialogProps) {
  const { user } = useAuth();
  const { currentResidence, residences } = useUser();
  

  const [selectedActivity, setSelectedActivity] = useState<ActivityData | null>(null);
  const [activityLikesCount, setActivityLikesCount] = useState<number>(0);
  const [activityComments, setActivityComments] = useState<Array<{
    id: string;
    content: string;
    authorName: string;
    createdAt: string;
    likes: number;
    hasLiked: boolean;
  }>>([]);
  const [activityCommentLoading, setActivityCommentLoading] = useState<boolean>(false);
  const [newActivityComment, setNewActivityComment] = useState<string>('');
  const [userHasLikedActivity, setUserHasLikedActivity] = useState<boolean>(false);
  
  const { likeComment, likePost } = useForumStore();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<string>('');
  const [editContent, setEditContent] = useState<string>('');
  const [editEventStart, setEditEventStart] = useState<string>('');
  const [editEventEnd, setEditEventEnd] = useState<string>('');
  const [savingEdit, setSavingEdit] = useState<boolean>(false);

  // Event-specific state
  const [attendanceDetails, setAttendanceDetails] = useState<{ 
    attending: Array<{id: string; name: string}>; 
    declined: Array<{id: string; name: string; explanation?: string}>; 
    notReplied: Array<{id: string; name: string}> 
  }>({ attending: [], declined: [], notReplied: [] });
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  
  const { toggleAttendance, getAttendanceStatus } = useEventStore();
  const queryClient = useQueryClient();
  const [showExplanationDialog, setShowExplanationDialog] = useState(false);
  const [explanationText, setExplanationText] = useState('');
  const [pendingEventId, setPendingEventId] = useState<string | null>(null);

  // Robust hent navn fra profiles join (kan være objekt eller array)
  function getProfileName(profileLike: any): string {
    try {
      const p = Array.isArray(profileLike) ? profileLike[0] : profileLike;
      const first = p?.first_name || '';
      const last = p?.last_name || '';
      return `${first} ${last}`.trim();
    } catch {
      return '';
    }
  }

  const dialogOpen = open;

  useEffect(() => {
    if (!dialogOpen || !activityId) {
      // Nullstill state når dialogen lukkes eller activityId er null
      setSelectedActivity(null);
      setActivityComments([]);
      setActivityLikesCount(0);
      setUserHasLikedActivity(false);
      setAttendanceDetails({ attending: [], declined: [], notReplied: [] });
      return;
    }
    
    // Nullstill selectedActivity umiddelbart når et nytt event åpnes
    setSelectedActivity(null);
    
    async function loadActivity(id: number) {
    try {
      // Determine table based on activity type
      const tableName = activityType === 'news' ? 'residence_news' : 'base_post';
      
      // 1. Hent hoveddata først - dette trenger vi for å vise innholdet raskt
      const { data: activityData, error: activityError } = await (supabase as any)
        .from(tableName)
        .select(`
          id,
          title,
          content,
          user_id,
          created_at,
          updated_at,
          ${activityType !== 'news' ? 'board_initiated,' : ''}
          profiles!user_id(first_name, last_name)
          ${activityType === 'event' ? ',forum_event!inner(explanation, event_start, event_end)' : ''}
        `)
        .eq('id', id)
        .single();
      
      if (activityError) throw activityError;

      // Sett hoveddata umiddelbart så brukeren ser innholdet raskt
      setSelectedActivity({
        id: activityData.id,
        title: activityData.title,
        content: activityData.content,
        user_id: activityData.user_id,
        created_at: activityData.created_at,
        updated_at: activityData.updated_at,
        author_name: `${activityData.profiles?.first_name || ''} ${activityData.profiles?.last_name || ''}`.trim(),
        event_start: activityData.forum_event?.event_start,
        event_end: activityData.forum_event?.event_end,
        explanation: activityData.forum_event?.explanation,
        attendees: 0, // Will be oppdatert fra attendance data
        board_initiated: activityType === 'news' ? true : activityData.board_initiated
      });

      setEditTitle(activityData.title);
      setEditContent(activityData.content);
      
      // Set event dates for editing
      if (activityType === 'event' && activityData.forum_event) {
        setEditEventStart(activityData.forum_event.event_start ? 
          format(new Date(activityData.forum_event.event_start), "yyyy-MM-dd'T'HH:mm") : '');
        setEditEventEnd(activityData.forum_event.event_end ? 
          format(new Date(activityData.forum_event.event_end), "yyyy-MM-dd'T'HH:mm") : '');
      }

      // 2. Gjør alle andre queries parallelt for raskere lasting
      const queries: Promise<any>[] = [];

      // Likes query
      if (showLikes && (activityType === 'post' || activityType === 'event')) {
        queries.push(
          supabase
          .from('likes')
          .select('*', { count: 'exact', head: true })
          .eq('post_id', id)
            .eq('is_liked', true)
            .then(({ count }) => {
              setActivityLikesCount(count || 0);
              return count;
            })
        );

        // User liked query
        if (user?.id) {
          queries.push(
            supabase
            .from('likes')
            .select('user_id')
            .eq('post_id', id)
            .eq('user_id', user.id)
            .eq('is_liked', true)
              .maybeSingle()
              .then(({ data }) => {
                setUserHasLikedActivity(!!data);
                return data;
              })
          );
        } else {
          setUserHasLikedActivity(false);
        }
      }

      // Comments query - hent med likes-data (samme som Forum)
      if (showComments && (activityType === 'post' || activityType === 'event')) {
        queries.push(
          (supabase as any)
          .from('comment')
          .select(`
            id,
            post_id,
            user_id,
            content,
            created_at,
            updated_at,
            user:user_id (
              id,
              first_name,
              last_name
            ),
            comment_likes:comment_like (
              comment_id,
              user_id,
              is_liked
            )
          `)
          .eq('post_id', id)
            .order('created_at', { ascending: true })
            .then(({ data, error }) => {
              if (error) throw error;
              
              // Transform kommentarer til riktig format med likes (samme som Forum)
              const transformedComments = (data || []).map((comment: any) => {
                // Beregn likes (kun is_liked = true)
                const likes = comment.comment_likes?.filter((like: any) => like.is_liked).length || 0;
                
                // Sjekk om brukeren har likt denne kommentaren
                const hasLiked = user?.id ? comment.comment_likes?.some((like: any) => 
                  like.user_id === user.id && like.is_liked
                ) || false : false;

                return {
                  id: comment.id.toString(),
                  content: comment.content,
                  authorName: `${comment.user?.first_name || ''} ${comment.user?.last_name || ''}`.trim(),
                  createdAt: comment.created_at,
                  likes,
                  hasLiked
                };
              });
              
              setActivityComments(transformedComments);
              return transformedComments;
            })
        );
      }

      // Event attendance query (kan gjøres parallelt med resten)
      if (activityType === 'event' && showAttendance) {
        queries.push(loadEventAttendanceData(id));
      }

      // Vent på alle queries i parallell
      await Promise.all(queries);

    } catch (error) {
      console.error('Error loading activity:', error);
    }
    }
    
    void loadActivity(activityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, activityId, activityType, showLikes, showComments, showAttendance, user?.id, currentResidence?.role, residences]);

  async function loadEventAttendanceData(eventId: number) {
    try {
      setAttendanceLoading(true);
      
      // Load attendance details - hent alle entries først
      const { data: attendanceData, error: attendanceError } = await supabase
        .from('event_attendance')
        .select(`
          user_id,
          state,
          explanation
        `)
        .eq('post_id', eventId);

      if (attendanceError) {
        console.error('Error loading attendance data:', attendanceError);
        setAttendanceLoading(false);
        return;
      }

      if (!attendanceData || attendanceData.length === 0) {
        setAttendanceDetails({ attending: [], declined: [], notReplied: [] });
        setSelectedActivity(prev => prev ? { ...prev, attendees: 0 } : null);
        setAttendanceLoading(false);
        return;
      }

      // Hent alle unike user_ids
      const userIds = [...new Set(attendanceData.map(a => a.user_id))];
      
      // Hent profilene for alle brukere - kan gjøres parallelt hvis vi hadde flere queries
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      // Opprett en map for rask oppslag
      const profilesMap = new Map(
        (profilesData || []).map(p => [p.id, { first_name: p.first_name, last_name: p.last_name }])
      );

      // Hjelpefunksjon for å hente navn
      const getName = (userId: string): string => {
        const profile = profilesMap.get(userId);
        if (profile) {
          const name = getProfileName(profile);
          if (name.trim()) {
            return name;
          }
        }
        // Fallback hvis profil mangler eller ikke har navn
        return 'Bruker';
      };

        const attending = attendanceData
          .filter(a => a.state === 'attending')
        .map(a => ({ 
          id: a.user_id, 
          name: getName(a.user_id) 
        }));
        
        const declined = attendanceData
          .filter(a => a.state === 'declined')
          .map(a => ({ 
            id: a.user_id, 
          name: getName(a.user_id),
            explanation: a.explanation 
          }));
        
        const notReplied = attendanceData
          .filter(a => a.state === 'not_replied')
        .map(a => ({ 
          id: a.user_id, 
          name: getName(a.user_id) 
        }));

        setAttendanceDetails({ attending, declined, notReplied });
        setSelectedActivity(prev => prev ? { ...prev, attendees: attending.length } : null);
    } catch (error) {
      console.error('Error loading attendance data:', error);
    } finally {
      setAttendanceLoading(false);
    }
  }

  const handleLikeActivity = async () => {
    if (!user?.id || !selectedActivity || (activityType !== 'post' && activityType !== 'event')) return;

    const wasLiked = userHasLikedActivity;
    const newLikeState = !wasLiked;
    
    // Optimistisk oppdatering - oppdater UI umiddelbart
    setUserHasLikedActivity(newLikeState);
    setActivityLikesCount(prev => newLikeState ? prev + 1 : prev - 1);

    try {
      // Bruk samme logikk som Forum for å sikre synkronisering (i bakgrunnen)
      await likePost(selectedActivity.id.toString());

      // Synkroniser med database i bakgrunnen for å sikre nøyaktighet
      // (men UI er allerede oppdatert, så brukeren ser endringen umiddelbart)
      const { count } = await supabase
        .from('likes')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', selectedActivity.id)
        .eq('is_liked', true);

      if (count !== null) {
        setActivityLikesCount(count);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Rull tilbake ved feil
      setUserHasLikedActivity(wasLiked);
      setActivityLikesCount(prev => wasLiked ? prev + 1 : prev - 1);
    }
  };

  const handleDelete = async () => {
    if (!selectedActivity) return;
    // Sikkerhetsbekreftelse
    const confirmed = window.confirm('Er du sikker på at du vil slette denne aktiviteten? Dette kan ikke angres.');
    if (!confirmed) return;

    try {
      const canDelete = (() => {
        const isOwner = user?.id === selectedActivity.user_id;
        const isBoardEditable = selectedActivity.board_initiated || activityType === 'news';
        const userIsBoard = (currentResidence?.role === 'board') || (residences || []).some(r => r.role === 'board');
        return (isBoardEditable && userIsBoard) || isOwner;
      })();
      if (!canDelete) return;

      if (activityType === 'news') {
        const { error } = await (supabase as any)
          .from('residence_news')
          .delete()
          .eq('id', selectedActivity.id);
        if (error) throw error;
        window.dispatchEvent(new CustomEvent('newsUpdated', { detail: { activityId: selectedActivity.id } }));
      } else {
        const { error } = await (supabase as any)
          .from('base_post')
          .delete()
          .eq('id', selectedActivity.id);
        if (error) throw error;
        const evtName = activityType === 'event' ? 'eventUpdated' : 'postUpdated';
        window.dispatchEvent(new CustomEvent(evtName, { detail: { activityId: selectedActivity.id } }));
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Feil ved sletting av aktivitet:', error);
    }
  };

  const handleAttendance = async (e: React.MouseEvent, status?: 'attending' | 'declined') => {
    e.stopPropagation();
    if (!selectedActivity) return;
    const eventId = selectedActivity.id.toString();
    try {
      if (status === 'declined') {
        const requires = selectedActivity.explanation === true;
        if (requires) {
          setPendingEventId(eventId);
          setShowExplanationDialog(true);
          return;
        }
      }
      await toggleAttendance(eventId, status, status === 'declined' ? explanationText : undefined);
      if (activityType === 'event' && showAttendance) {
        await loadEventAttendanceData(selectedActivity.id);
      }
    } catch (error: any) {
      if (error?.message === 'Begrunnelse kreves for å avslå dette eventet') {
        setPendingEventId(eventId);
        setShowExplanationDialog(true);
      } else {
        console.error('Feil ved toggle av event attendance:', error);
      }
    }
  };

  const handleExplanationSubmit = async () => {
    if (!pendingEventId || !explanationText.trim()) return;
    try {
      await toggleAttendance(pendingEventId, 'declined', explanationText);
      setShowExplanationDialog(false);
      setExplanationText('');
      setPendingEventId(null);
      if (activityType === 'event' && showAttendance && selectedActivity) {
        await loadEventAttendanceData(selectedActivity.id);
      }
      // Invalider cache for events i EventsSection
      if (currentResidence?.residence_id) {
        await queryClient.invalidateQueries({ queryKey: ['residence', currentResidence.residence_id, 'events'] });
      }
    } catch (error) {
      console.error('Feil ved sending av begrunnelse:', error);
    }
  };

  const handleAddComment = async () => {
    if (!user?.id || !selectedActivity || !newActivityComment.trim() || (activityType !== 'post' && activityType !== 'event')) return;

    try {
      setActivityCommentLoading(true);

      const { data: commentData, error: commentError } = await (supabase as any)
        .from('comment')
        .insert({
          post_id: selectedActivity.id,
          user_id: user.id,
          content: newActivityComment.trim()
        })
        .select(`
          id,
          post_id,
          user_id,
          content,
          created_at,
          updated_at,
          user:user_id (
            id,
            first_name,
            last_name
          ),
          comment_likes:comment_like (
            comment_id,
            user_id,
            is_liked
          )
        `)
        .single();

      if (commentError) throw commentError;

      // Transform ny kommentar til riktig format med likes
      const transformedComment = {
        id: commentData.id.toString(),
        content: commentData.content,
        authorName: `${commentData.user?.first_name || ''} ${commentData.user?.last_name || ''}`.trim(),
        createdAt: commentData.created_at,
        likes: commentData.comment_likes?.filter((like: any) => like.is_liked).length || 0,
        hasLiked: false // Ny kommentar, brukeren har ikke likt den ennå
      };

      setActivityComments(prev => [...prev, transformedComment]);
      setNewActivityComment('');

    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setActivityCommentLoading(false);
    }
  };

  const handleCommentLike = async (commentId: string) => {
    if (!user?.id || !selectedActivity) return;

    const comment = activityComments.find(c => c.id === commentId);
    if (!comment) return;

    const wasLiked = comment.hasLiked;
    const newHasLiked = !wasLiked;

    // Optimistisk oppdatering - oppdater UI umiddelbart
    setActivityComments(prev => prev.map(c => {
      if (c.id === commentId) {
        return {
          ...c,
          hasLiked: newHasLiked,
          likes: newHasLiked ? c.likes + 1 : c.likes - 1
        };
      }
      return c;
    }));

    try {
      // Synkroniser med database i bakgrunnen
      await likeComment(commentId);
    } catch (error) {
      console.error('Error toggling comment like:', error);
      // Rull tilbake ved feil
      setActivityComments(prev => prev.map(c => {
        if (c.id === commentId) {
          return {
            ...c,
            hasLiked: wasLiked,
            likes: wasLiked ? c.likes + 1 : c.likes - 1
          };
        }
        return c;
      }));
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedActivity || !editTitle.trim() || !editContent.trim()) return;

    // Validate event dates
    if (activityType === 'event' && editEventStart && editEventEnd) {
      const startDate = new Date(editEventStart);
      const endDate = new Date(editEventEnd);
      if (endDate <= startDate) {
        alert('Sluttid må være etter starttid');
        return;
      }
    }

    try {
      setSavingEdit(true);

      const tableName = activityType === 'news' ? 'residence_news' : 'base_post';
      
      // Update base_post or residence_news
      const { error } = await (supabase as any)
        .from(tableName)
        .update({
          title: editTitle.trim(),
          content: editContent.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedActivity.id);

      if (error) throw error;

      // Update forum_event if this is an event
      if (activityType === 'event' && (editEventStart || editEventEnd)) {
        const eventData: any = {};
        
        if (editEventStart) {
          eventData.event_start = new Date(editEventStart).toISOString();
        }
        if (editEventEnd) {
          eventData.event_end = new Date(editEventEnd).toISOString();
        }

        const { error: eventError } = await supabase
          .from('forum_event')
          .update(eventData)
          .eq('id', selectedActivity.id);

        if (eventError) throw eventError;
      }

      // Update local state
      setSelectedActivity(prev => prev ? {
        ...prev,
        title: editTitle.trim(),
        content: editContent.trim(),
        updated_at: new Date().toISOString(),
        ...(activityType === 'event' && {
          event_start: editEventStart ? new Date(editEventStart).toISOString() : prev.event_start,
          event_end: editEventEnd ? new Date(editEventEnd).toISOString() : prev.event_end
        })
      } : null);

      setEditMode(false);

      // Trigger window event for å oppdatere aktivitetslisten
      console.log(`ActivityDialog: Dispatching ${activityType}Updated event`);
      window.dispatchEvent(new CustomEvent(`${activityType}Updated`, { 
        detail: { activityId: selectedActivity.id } 
      }));

    } catch (error) {
      console.error('Error saving edit:', error);
    } finally {
      setSavingEdit(false);
    }
  };

  if (!dialogOpen) return null;

  // Vis loading state mens data lastes
  if (!selectedActivity) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-condo-dark mx-auto mb-4"></div>
              <p className="text-gray-600">Laster arrangement...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold">{editMode ? editTitle : selectedActivity.title}</h3>
          <div className="flex flex-col items-end gap-2">
            <button onClick={() => onOpenChange(false)} className="text-gray-500 hover:text-gray-700">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-600">
            {(() => {
              const created = new Date(selectedActivity.created_at);
              const updated = selectedActivity.updated_at ? new Date(selectedActivity.updated_at) : null;
              const createdDay = format(created, 'yyyy-MM-dd');
              const updatedDay = updated ? format(updated, 'yyyy-MM-dd') : null;
              const createdText = `Opprettet av ${selectedActivity.author_name} • ${format(created, 'd. MMM yyyy HH:mm', { locale: nb })}`;
              const updatedText = updated && updatedDay !== createdDay
                ? ` • Oppdatert ${format(updated, 'd. MMM yyyy HH:mm', { locale: nb })}`
                : '';
              return (
                <span>
                  {createdText}
                  {updatedText}
                </span>
              );
            })()}
          </div>
          {showEditButton && !editMode && ( (() => {
            const isOwner = user?.id === selectedActivity.user_id;
            const isBoardEditable = selectedActivity.board_initiated || activityType === 'news';
            const userIsBoard = (currentResidence?.role === 'board') || (residences || []).some(r => r.role === 'board');
            const canEdit = (isBoardEditable && userIsBoard) || isOwner;
            return canEdit;
          })() ) && (
            <Button size="sm" onClick={() => setEditMode(true)}>
              Rediger {activityType === 'news' ? 'nyhet' : activityType === 'event' ? 'arrangement' : 'innlegg'}
            </Button>
          )}
          {/* Edit-mode handling flyttes til bunnkontroller */}
        </div>

        {!editMode ? (
          <>
            {/* Event-specific info */}
            {activityType === 'event' && selectedActivity.event_start && (
              <div className="mb-4 space-y-2">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  {selectedActivity.event_start && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>{format(new Date(selectedActivity.event_start), 'dd. MMM yyyy', { locale: nb })}</span>
                    </div>
                  )}
                  {selectedActivity.event_start && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{format(new Date(selectedActivity.event_start), 'HH:mm', { locale: nb })}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="prose max-w-none text-gray-800 leading-7">
              <p className="whitespace-pre-line">{selectedActivity.content}</p>
            </div>

            {/* Event attendance section */}
            {activityType === 'event' && showAttendance && (
              <div className="py-2 border-t mt-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Users className="h-4 w-4" />
                    <span>{attendanceDetails.attending.length} skal</span>
                  </div>
                  {/* Attendance buttons - samme stil som Hjem/Kommende Hendelser */}
                  <div className="flex items-center gap-2">
                    {/* Meld på */}
                    <button
                      onClick={(e) => {
                        if (getAttendanceStatus(selectedActivity.id.toString()) !== 'attending') {
                          void handleAttendance(e, 'attending');
                        }
                      }}
                      className={cn(
                        'flex items-center gap-1 text-sm transition-colors select-none',
                        'focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2 rounded',
                        getAttendanceStatus(selectedActivity.id.toString()) === 'attending'
                          ? 'text-green-600'
                          : getAttendanceStatus(selectedActivity.id.toString()) === 'declined'
                          ? 'text-gray-400 hover:text-gray-500'
                          : 'text-green-600 hover:text-green-700'
                      )}
                      aria-label="Meld på"
                    >
                      <Check className="h-3 w-3" aria-hidden="true" />
                      <span>
                        {getAttendanceStatus(selectedActivity.id.toString()) === 'attending' ? 'Meldt på' : 'Meld på'}
                      </span>
                    </button>

                    {/* Meld avbud */}
                    <button
                      onClick={(e) => {
                        if (getAttendanceStatus(selectedActivity.id.toString()) !== 'declined') {
                          void handleAttendance(e, 'declined');
                        }
                      }}
                      className={cn(
                        'flex items-center gap-1 text-sm transition-colors select-none',
                        'focus:outline-none focus:ring-2 focus:ring-condo-dark focus:ring-offset-2 rounded',
                        getAttendanceStatus(selectedActivity.id.toString()) === 'declined'
                          ? 'text-red-600'
                          : getAttendanceStatus(selectedActivity.id.toString()) === 'attending'
                          ? 'text-gray-400 hover:text-gray-500'
                          : 'text-red-600 hover:text-red-700'
                      )}
                      aria-label="Meld avbud"
                    >
                      <XIcon className="h-3 w-3" aria-hidden="true" />
                      <span>
                        {getAttendanceStatus(selectedActivity.id.toString()) === 'declined' ? 'Meldt avbud' : 'Meld avbud'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Full oversikt: vis for styre-initiert ELLER når forfatteren ser sitt eget event */}
                {(selectedActivity.board_initiated || (user?.id && user.id === selectedActivity.user_id)) && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                    <div>
                      <span className="text-sm font-medium text-green-600">Skal ({attendanceDetails.attending.length})</span>
                      <div className="mt-2 space-y-1">
                        {attendanceLoading ? (
                          <div className="text-sm text-gray-500">Laster…</div>
                        ) : attendanceDetails.attending.length === 0 ? (
                          <div className="text-sm text-gray-500">Ingen</div>
                        ) : (
                          attendanceDetails.attending.map((p, index) => (
                            <div key={`${p.id}-${index}`} className="text-sm text-gray-700">{p.name}</div>
                          ))
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-red-600">Avslått ({attendanceDetails.declined.length})</span>
                      <div className="mt-2 space-y-1">
                        {attendanceLoading ? (
                          <div className="text-sm text-gray-500">Laster…</div>
                        ) : attendanceDetails.declined.length === 0 ? (
                          <div className="text-sm text-gray-500">Ingen</div>
                        ) : (
                          attendanceDetails.declined.map((p, index) => (
                            <div key={`${p.id}-${index}`} className="text-sm text-gray-700">
                              <div>{p.name}</div>
                              {p.explanation && (
                                <div className="text-xs text-gray-500">Begrunnelse: {p.explanation}</div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-500">Ubesvart ({attendanceDetails.notReplied.length})</span>
                      <div className="mt-2 space-y-1">
                        {attendanceLoading ? (
                          <div className="text-sm text-gray-500">Laster…</div>
                        ) : attendanceDetails.notReplied.length === 0 ? (
                          <div className="text-sm text-gray-500">Ingen</div>
                        ) : (
                          attendanceDetails.notReplied.map((p, index) => (
                            <div key={`${p.id}-${index}`} className="text-sm text-gray-700">{p.name}</div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SAMMENSATT LIKES OG COMMENTS SEKSJON */}
            {((activityType === 'post' || activityType === 'event') && (showLikes || showComments)) && (
              <div className="space-y-2 mt-6">
                <div className="flex items-center justify-between py-2 border-t">
                  <div className="flex items-center gap-4">
                    {showLikes && (
                      <LikeButton 
                        count={activityLikesCount}
                        hasReacted={userHasLikedActivity}
                        onClick={handleLikeActivity}
                        variant="forum"
                      />
                    )}
                    {showComments && (
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <MessageCircle className="h-4 w-4" />
                        <span>{activityComments.length} Kommentarer</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {showComments && (
                  <>
                    <div className="space-y-3">
                      {activityCommentLoading ? (
                        <div className="text-sm text-gray-500">Laster kommentarer...</div>
                      ) : (
                        activityComments.map(c => (
                          <Comment 
                            key={c.id} 
                            comment={c} 
                            onLike={handleCommentLike}
                            variant="forum" 
                          />
                        ))
                      )}
                    </div>
                    <CommentForm 
                      value={newActivityComment}
                      onChange={setNewActivityComment}
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (newActivityComment.trim()) {
                          void handleAddComment();
                        }
                      }}
                      variant="forum"
                    />
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-condo-dark focus:border-transparent"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Tittel"
            />
            
            {/* Event-specific date/time fields */}
            {activityType === 'event' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Starttid
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-condo-dark focus:border-transparent"
                    value={editEventStart}
                    onChange={(e) => setEditEventStart(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sluttid (valgfritt)
                  </label>
                  <input
                    type="datetime-local"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-condo-dark focus:border-transparent"
                    value={editEventEnd}
                    onChange={(e) => setEditEventEnd(e.target.value)}
                  />
                </div>
              </div>
            )}
            
            <textarea
              className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-condo-dark focus:border-transparent min-h-[140px]"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Innhold"
            />
            {/* Bottom controls: Slett (venstre), Avbryt/Lagre (høyre) */}
            {(() => {
              const isOwner = user?.id === selectedActivity.user_id;
              const isBoardEditable = selectedActivity.board_initiated || activityType === 'news';
              const userIsBoard = (currentResidence?.role === 'board') || (residences || []).some(r => r.role === 'board');
              const canEdit = (isBoardEditable && userIsBoard) || isOwner;
              return canEdit;
            })() && (
              <div className="flex items-center justify-between pt-2">
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => { void handleDelete(); }}
                >
                  Slett
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditMode(false);
                      setEditTitle(selectedActivity.title || '');
                      setEditContent(selectedActivity.content || '');
                      // Reset event dates
                      if (activityType === 'event' && selectedActivity.event_start) {
                        setEditEventStart(format(new Date(selectedActivity.event_start), "yyyy-MM-dd'T'HH:mm"));
                      } else {
                        setEditEventStart('');
                      }
                      if (activityType === 'event' && selectedActivity.event_end) {
                        setEditEventEnd(format(new Date(selectedActivity.event_end), "yyyy-MM-dd'T'HH:mm"));
                      } else {
                        setEditEventEnd('');
                      }
                    }}
                  >
                    Avbryt
                  </Button>
                  <Button onClick={handleSaveEdit} disabled={savingEdit}>
                    {savingEdit ? 'Lagrer…' : 'Lagre'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Explanation Dialog */}
      <Dialog 
        open={showExplanationDialog} 
        onOpenChange={setShowExplanationDialog}
        title="Begrunnelse påkrevd"
      >
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Begrunnelse påkrevd</h3>
          <p className="text-gray-600 mb-4">
            Dette arrangementet krever en begrunnelse når du avslår. Vennligst forklar hvorfor du ikke kan delta.
          </p>
          <Textarea
            value={explanationText}
            onChange={(e: any) => setExplanationText(e.target.value)}
            placeholder="Skriv din begrunnelse her..."
            rows={4}
            className="mb-4"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowExplanationDialog(false);
                setExplanationText('');
                setPendingEventId(null);
              }}
            >
              Avbryt
            </Button>
            <Button
              onClick={handleExplanationSubmit}
              disabled={!explanationText.trim()}
            >
              Send begrunnelse
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}